import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { gql } from '@apollo/client';
import { generateClient } from 'aws-amplify/api';
import { ClientTypeBadge, CaseStatusType } from '../components/chat/ClientTypeBadge';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_MY_MESSAGES = gql`
  query ListMyMessages($limit: Int) {
    listMyMessages(limit: $limit) {
      items {
        id
        conversationId
        clientId
        staffId
        senderId
        recipientId
        content
        isRead
        isEdited
        createdAt
      }
    }
  }
`;

const LIST_CASES = gql`
  query ListCases($limit: Int) {
    listCases(limit: $limit) {
      items {
        id
        caseNumber
        status
        clientId
        client {
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`;

const LIST_STAFF = gql`
  query ListStaff {
    listStaff {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

const LIST_CLIENTS = gql`
  query ListClients {
    listClients {
      id
      firstName
      lastName
      email
      role
      isEnabled
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

const MARK_MESSAGE_AS_READ = gql`
  mutation MarkMessageAsRead($id: ID!, $conversationId: ID!, $sentAt: AWSDateTime!) {
    markMessageAsRead(id: $id, conversationId: $conversationId, sentAt: $sentAt) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

const DELETE_MESSAGE = gql`
  mutation DeleteMessage($id: ID!, $conversationId: ID!, $sentAt: AWSDateTime!) {
    deleteMessage(id: $id, conversationId: $conversationId, sentAt: $sentAt)
  }
`;

const EDIT_MESSAGE = gql`
  mutation EditMessage($id: ID!, $conversationId: ID!, $sentAt: AWSDateTime!, $content: String!) {
    editMessage(id: $id, conversationId: $conversationId, sentAt: $sentAt, content: $content) {
      id
      conversationId
      clientId
      staffId
      senderId
      recipientId
      content
      isRead
      isEdited
      createdAt
    }
  }
`;

interface Message {
  id: string;
  conversationId: string;
  clientId: string;
  staffId: string;
  senderId: string;
  recipientId: string;
  content: string;
  isRead: boolean;
  isEdited?: boolean;
  createdAt: string;
}

interface Conversation {
  conversationId: string;
  otherUserId: string;
  otherUserName: string;
  otherUserEmail: string;
  otherUserRole?: string;
  otherUserIsEnabled?: boolean;
  caseId?: string;
  caseNumber?: string;
  caseStatus?: CaseStatusType | string;
  lastMessage: string;
  lastMessageDate: Date;
  unreadCount: number;
  messages: Message[];
  isStaffConversation: boolean;
}

export function MessagesList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [editText, setEditText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const markedAsReadRef = useRef<Set<string>>(new Set());

  const { data: userData, loading: userLoading } = useQuery<{ getCurrentUser: { id: string; email: string; firstName: string; lastName: string; role: string } }>(GET_CURRENT_USER);

  const { data: messagesData, loading: messagesLoading } = useQuery<{ listMyMessages: { items: Message[] } }>(LIST_MY_MESSAGES, {
    variables: { limit: 500 },
    fetchPolicy: 'cache-and-network',
  });

  const { data: casesData, loading: casesLoading } = useQuery<{ listCases: { items: any[] } }>(LIST_CASES, {
    variables: { limit: 200 },
    fetchPolicy: 'cache-and-network',
  });

  const { data: staffData, loading: staffLoading } = useQuery<{ listStaff: any[] }>(LIST_STAFF, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: clientsData, loading: clientsLoading } = useQuery<{ listClients: any[] }>(LIST_CLIENTS, {
    fetchPolicy: 'cache-and-network',
  });

  const apolloClient = useApolloClient();

  // Helper function to add message to Apollo cache
  const addMessageToCache = useCallback((newMessage: Message) => {
    apolloClient.cache.updateQuery<{ listMyMessages: { items: Message[] } }>(
      { query: LIST_MY_MESSAGES, variables: { limit: 500 } },
      (existingData) => {
        if (!existingData?.listMyMessages?.items) {
          return existingData;
        }
        
        // Check if message already exists
        const exists = existingData.listMyMessages.items.some(
          (msg: Message) => msg.id === newMessage.id
        );
        if (exists) {
          return existingData;
        }
        
        return {
          ...existingData,
          listMyMessages: {
            ...existingData.listMyMessages,
            items: [...existingData.listMyMessages.items, newMessage],
          },
        };
      }
    );
  }, [apolloClient]);

  const [sendMessage, { loading: sending }] = useMutation<{ sendMessage: Message }>(SEND_MESSAGE, {
    onCompleted: (data) => {
      setMessageText('');
      // Add sent message to cache directly instead of refetching
      if (data?.sendMessage) {
        addMessageToCache(data.sendMessage);
      }
    },
  });

  const [markAsRead] = useMutation<{ markMessageAsRead: Message }>(MARK_MESSAGE_AS_READ, {
    // Remove refetchQueries to reduce API calls - cache will be updated via subscription
  });

  const [deleteMessage, { loading: deleting }] = useMutation<{ deleteMessage: boolean }>(DELETE_MESSAGE);

  const [editMessage, { loading: editing }] = useMutation<{ editMessage: Message }>(EDIT_MESSAGE);

  // Helper function to update message read status in Apollo cache
  // Uses cache.modify to update the message directly by ID, which updates all queries
  const updateMessageReadStatusInCache = useCallback((messageId: string) => {
    apolloClient.cache.modify({
      id: apolloClient.cache.identify({ __typename: 'Message', id: messageId }),
      fields: {
        isRead() {
          return true;
        },
      },
    });
  }, [apolloClient]);

  // Helper function to remove message from Apollo cache
  const removeMessageFromCache = useCallback((messageId: string) => {
    apolloClient.cache.updateQuery<{ listMyMessages: { items: Message[] } }>(
      { query: LIST_MY_MESSAGES, variables: { limit: 500 } },
      (existingData) => {
        if (!existingData?.listMyMessages?.items) {
          return existingData;
        }
        
        return {
          ...existingData,
          listMyMessages: {
            ...existingData.listMyMessages,
            items: existingData.listMyMessages.items.filter(
              (msg: Message) => msg.id !== messageId
            ),
          },
        };
      }
    );
  }, [apolloClient]);

  // Handle message deletion
  const handleDeleteMessage = async (message: Message) => {
    if (!confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await deleteMessage({
        variables: {
          id: message.id,
          conversationId: message.conversationId,
          sentAt: message.createdAt,
        },
      });

      // Remove from cache
      removeMessageFromCache(message.id);

      // Update selected conversation
      if (selectedConversation) {
        setSelectedConversation(prev => {
          if (!prev) return prev;
          const updatedMessages = prev.messages.filter(m => m.id !== message.id);
          const lastMsg = updatedMessages[updatedMessages.length - 1];
          return {
            ...prev,
            messages: updatedMessages,
            lastMessage: lastMsg?.content || '',
            lastMessageDate: lastMsg ? new Date(lastMsg.createdAt) : prev.lastMessageDate,
          };
        });
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  // Start editing a message
  const handleStartEdit = (message: Message) => {
    setEditingMessage(message);
    setEditText(message.content);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  // Save edited message
  const handleSaveEdit = async () => {
    if (!editingMessage || !editText.trim()) return;

    try {
      const result = await editMessage({
        variables: {
          id: editingMessage.id,
          conversationId: editingMessage.conversationId,
          sentAt: editingMessage.createdAt,
          content: editText.trim(),
        },
      });

      // Update cache
      if (result.data?.editMessage) {
        apolloClient.cache.modify({
          id: apolloClient.cache.identify({ __typename: 'Message', id: editingMessage.id }),
          fields: {
            content() {
              return editText.trim();
            },
            isEdited() {
              return true;
            },
          },
        });

        // Update selected conversation
        if (selectedConversation) {
          setSelectedConversation(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: prev.messages.map(m =>
                m.id === editingMessage.id
                  ? { ...m, content: editText.trim(), isEdited: true }
                  : m
              ),
            };
          });
        }
      }

      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      console.error('Failed to edit message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  // Subscribe to new messages using Amplify's GraphQL API
  useEffect(() => {
    if (!userData?.getCurrentUser?.id) return;

    const client = generateClient();
    
    // Create subscription
    const sub = client.graphql({
      query: `
        subscription OnMessageReceived($recipientId: ID!) {
          onMessageReceived(recipientId: $recipientId) {
            id
            conversationId
            clientId
            staffId
            senderId
            recipientId
            content
            isRead
            createdAt
          }
        }
      `,
      variables: {
        recipientId: userData.getCurrentUser.id,
      },
    });

    // Handle subscription
    const subscription = (sub as any).subscribe({
      next: ({ data }: any) => {
        console.log('New message received via subscription:', data);
        if (data?.onMessageReceived) {
          const newMessage = data.onMessageReceived;
          
          // Update Apollo cache directly instead of refetching
          addMessageToCache(newMessage);
          
          // Update the selected conversation if this message belongs to it
          if (selectedConversation && newMessage.conversationId === selectedConversation.conversationId) {
            setSelectedConversation(prev => {
              if (!prev) return prev;
              // Check if message already exists to avoid duplicates
              const exists = prev.messages.some(msg => msg.id === newMessage.id);
              if (exists) return prev;
              
              return {
                ...prev,
                messages: [...prev.messages, newMessage],
                lastMessage: newMessage.content,
                lastMessageDate: new Date(newMessage.createdAt),
              };
            });
          }
        }
      },
      error: (error: any) => {
        console.error('Subscription error:', error);
      },
    });

    return () => subscription.unsubscribe();
  }, [userData, addMessageToCache, selectedConversation]);

  // Subscribe to read receipts - when recipient reads our messages
  useEffect(() => {
    if (!userData?.getCurrentUser?.id) return;

    const client = generateClient();
    
    // Create read receipt subscription
    const sub = client.graphql({
      query: `
        subscription OnMessageRead($senderId: ID!) {
          onMessageRead(senderId: $senderId) {
            id
            conversationId
            clientId
            staffId
            senderId
            recipientId
            content
            isRead
            createdAt
          }
        }
      `,
      variables: {
        senderId: userData.getCurrentUser.id,
      },
    });

    // Handle subscription
    const subscription = (sub as any).subscribe({
      next: ({ data }: any) => {
        console.log('Read receipt received via subscription:', data);
        if (data?.onMessageRead) {
          const readMessage = data.onMessageRead;
          
          // Update Apollo cache directly
          updateMessageReadStatusInCache(readMessage.id);
          
          // Update the selected conversation if this message belongs to it
          if (selectedConversation && readMessage.conversationId === selectedConversation.conversationId) {
            setSelectedConversation(prev => {
              if (!prev) return prev;
              return {
                ...prev,
                messages: prev.messages.map(msg =>
                  msg.id === readMessage.id ? { ...msg, isRead: true } : msg
                ),
              };
            });
          }
        }
      },
      error: (error: any) => {
        console.error('Read receipt subscription error:', error);
      },
    });

    return () => subscription.unsubscribe();
  }, [userData, updateMessageReadStatusInCache, selectedConversation]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && userData?.getCurrentUser) {
      const currentUserId = userData.getCurrentUser.id;
      
      selectedConversation.messages.forEach((msg) => {
        // Only mark messages TO the current user (staff) that are unread
        if (!msg.isRead && msg.recipientId === currentUserId && !markedAsReadRef.current.has(msg.id)) {
          markedAsReadRef.current.add(msg.id);
          
          // Update cache immediately for responsive UI
          updateMessageReadStatusInCache(msg.id);
          
          markAsRead({
            variables: {
              id: msg.id,
              conversationId: msg.conversationId,
              sentAt: msg.createdAt,
            },
          }).catch((err) => {
            console.error('Failed to mark message as read:', err);
            markedAsReadRef.current.delete(msg.id);
          });
        }
      });
    }
  }, [selectedConversation, userData, markAsRead, updateMessageReadStatusInCache]);

  // Group messages by conversation
  const conversations: Conversation[] = [];
  const conversationMap = new Map<string, Conversation>();

  if (messagesData?.listMyMessages?.items && userData?.getCurrentUser) {
    const currentUserId = userData.getCurrentUser.id;
    const allUsers = new Map<string, any>();
    
    // Build user lookup map
    if (staffData?.listStaff) {
      staffData.listStaff.forEach((user: any) => allUsers.set(user.id, user));
    }
    if (clientsData?.listClients) {
      clientsData.listClients.forEach((user: any) => allUsers.set(user.id, user));
    }

    // Add current user to the map as well
    allUsers.set(currentUserId, userData.getCurrentUser);

    messagesData.listMyMessages.items.forEach((msg: any) => {
      const conversationId = msg.conversationId;
      
      // Determine the other user in the conversation
      const otherUserId = msg.senderId === currentUserId ? msg.recipientId : msg.senderId;
      const otherUser = allUsers.get(otherUserId);
      
      if (!otherUser) {
        // Create a placeholder for unknown users instead of skipping
        console.warn(`User ${otherUserId} not found in staff or clients list`);
        allUsers.set(otherUserId, {
          id: otherUserId,
          firstName: 'Unknown',
          lastName: 'User',
          email: 'unknown@example.com',
          role: 'UNKNOWN'
        });
      }

      const finalOtherUser = allUsers.get(otherUserId)!;

      // Check if this is a staff-to-staff conversation
      const isStaffConversation = conversationId.startsWith('staff#') && conversationId.includes('#staff#');

      if (!conversationMap.has(conversationId)) {
        // Find associated case if it's a client-staff conversation
        let caseInfo = null;
        if (msg.clientId && casesData?.listCases?.items) {
          caseInfo = casesData.listCases.items.find(
            (c: any) => c.clientId === msg.clientId
          );
        }

        conversationMap.set(conversationId, {
          conversationId,
          otherUserId,
          otherUserName: `${finalOtherUser.firstName} ${finalOtherUser.lastName}`,
          otherUserEmail: finalOtherUser.email,
          otherUserRole: finalOtherUser.role,
          otherUserIsEnabled: finalOtherUser.isEnabled ?? true,
          caseId: caseInfo?.id,
          caseNumber: caseInfo?.caseNumber,
          caseStatus: caseInfo?.status,
          lastMessage: msg.content,
          lastMessageDate: new Date(msg.createdAt),
          unreadCount: 0,
          messages: [],
          isStaffConversation,
        });
      }

      const conv = conversationMap.get(conversationId);
      if (conv) {
        conv.messages.push(msg);
        const msgDate = new Date(msg.createdAt);
        if (msgDate > conv.lastMessageDate) {
          conv.lastMessage = msg.content;
          conv.lastMessageDate = msgDate;
        }
        
        // Count unread messages TO the current user
        if (!msg.isRead && msg.recipientId === currentUserId) {
          conv.unreadCount++;
        }
      }
    });

    conversations.push(...Array.from(conversationMap.values()));
    
    // Sort messages within each conversation
    conversations.forEach(conv => {
      conv.messages.sort((a, b) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    });
  }

  // Sort conversations by most recent message
  conversations.sort((a, b) => b.lastMessageDate.getTime() - a.lastMessageDate.getTime());

  // Filter conversations
  const filteredConversations = conversations.filter((conv) =>
    conv.otherUserName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.otherUserEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (conv.caseNumber && conv.caseNumber.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Auto-select conversation when URL params are present
  useEffect(() => {
    const conversationIdParam = searchParams.get('conversationId');
    const userIdParam = searchParams.get('userId') || searchParams.get('clientId');
    
    if (!userData?.getCurrentUser) return;
    
    // Handle conversationId param (most reliable)
    if (conversationIdParam && !selectedConversation) {
      const conversation = conversations.find(conv => 
        conv.conversationId === conversationIdParam
      );
      
      if (conversation) {
        setSelectedConversation(conversation);
        setShowMobileSidebar(false);
      }
      
      // Clear the URL parameter after handling
      searchParams.delete('conversationId');
      navigate({ search: searchParams.toString() }, { replace: true });
      return;
    }
    
    // Handle userId/clientId param (fallback)
    if (userIdParam && !selectedConversation) {
      // Find existing conversation with this user
      const conversation = conversations.find(conv => 
        conv.otherUserId === userIdParam
      );
      
      if (conversation) {
        // Existing conversation found - select it
        setSelectedConversation(conversation);
        setShowMobileSidebar(false);
      }
      // Don't open new message modal - just go to messages page
      
      // Clear the URL parameter after handling
      searchParams.delete('userId');
      searchParams.delete('clientId');
      navigate({ search: searchParams.toString() }, { replace: true });
    }
  }, [searchParams, conversations, selectedConversation, userData, navigate]);

  const handleSendMessage = async () => {
    if (!selectedConversation || !messageText.trim()) return;

    try {
      const result = await sendMessage({
        variables: {
          input: {
            clientId: selectedConversation.isStaffConversation ? undefined : selectedConversation.otherUserId,
            recipientId: selectedConversation.otherUserId,
            content: messageText.trim(),
          },
        },
      });

      // Immediately add the sent message to the chat view
      if (result.data?.sendMessage) {
        const newMessage = result.data.sendMessage;
        setSelectedConversation(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [...prev.messages, newMessage],
            lastMessage: newMessage.content,
            lastMessageDate: new Date(newMessage.createdAt),
          };
        });
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleStartNewConversation = async () => {
    if (!selectedUserId || !messageText.trim()) {
      alert('Please select a user and enter a message');
      return;
    }

    try {
      const result = await sendMessage({
        variables: {
          input: {
            clientId: undefined, // Will be determined by backend based on recipient role
            recipientId: selectedUserId,
            content: messageText.trim(),
          },
        },
      });
      
      setShowNewMessageModal(false);
      setSelectedUserId('');
      setMessageText('');
      
      // Cache is updated via sendMessage's onCompleted callback
      // Find and select the new conversation after a brief delay to let cache update
      if (result.data?.sendMessage) {
        const newMsg = result.data.sendMessage;
        setTimeout(() => {
          const newConv = conversations.find(c => c.conversationId === newMsg.conversationId);
          if (newConv) {
            setSelectedConversation(newConv);
            setShowMobileSidebar(false);
          }
        }, 100);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aalaw-red"></div>
      </div>
    );
  }

  const user = userData?.getCurrentUser;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <Navigation user={user} />

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Conversations List */}
        <div className={`${
          showMobileSidebar ? 'block' : 'hidden'
        } md:block w-full md:w-80 bg-white border-r border-gray-200 flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
              <button
                onClick={() => setShowNewMessageModal(true)}
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
                className="px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">New</span>
              </button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </span>
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {messagesLoading || casesLoading || staffLoading || clientsLoading ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red mb-2"></div>
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'No conversations found' : 'No messages yet'}
                </p>
                <button
                  onClick={() => setShowNewMessageModal(true)}
                  style={{ backgroundColor: '#3B82F6', color: 'white' }}
                  className="mt-3 px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
                >
                  Start a conversation
                </button>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.conversationId}
                  onClick={() => {
                    setSelectedConversation(conversation);
                    setShowMobileSidebar(false);
                  }}
                  className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                    selectedConversation?.conversationId === conversation.conversationId
                      ? 'bg-blue-50 border-l-4 border-l-aalaw-red'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">
                          {conversation.otherUserName}
                        </p>
                        {/* Client Type Badge - show for client and prospect conversations */}
                        {!conversation.isStaffConversation && (conversation.otherUserRole === 'CLIENT' || conversation.otherUserRole === 'PROSPECT') && (
                          <ClientTypeBadge
                            caseStatus={conversation.caseStatus}
                            isEnabled={conversation.otherUserIsEnabled ?? true}
                            userRole={conversation.otherUserRole}
                            size="small"
                          />
                        )}
                        {conversation.unreadCount > 0 && (
                          <span className="bg-aalaw-red text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {conversation.isStaffConversation ? (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Staff • {conversation.otherUserRole}
                          </span>
                        ) : conversation.caseNumber ? (
                          `Case #${conversation.caseNumber}`
                        ) : (
                          conversation.otherUserEmail
                        )}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                      {formatMessageTime(conversation.lastMessageDate.toISOString())}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {conversation.lastMessage}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className={`${
          showMobileSidebar ? 'hidden' : 'flex'
        } md:flex flex-1 flex-col bg-white`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => setShowMobileSidebar(true)}
                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg -ml-2"
                  >
                    <span className="text-xl">←</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg truncate">{selectedConversation.otherUserName}</h3>
                      {/* Client Type Badge in chat header - show for client and prospect conversations */}
                      {!selectedConversation.isStaffConversation && (selectedConversation.otherUserRole === 'CLIENT' || selectedConversation.otherUserRole === 'PROSPECT') && (
                        <ClientTypeBadge
                          caseStatus={selectedConversation.caseStatus}
                          isEnabled={selectedConversation.otherUserIsEnabled ?? true}
                          userRole={selectedConversation.otherUserRole}
                          size="medium"
                        />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 truncate">
                      {selectedConversation.isStaffConversation ? (
                        `${selectedConversation.otherUserRole} • ${selectedConversation.otherUserEmail}`
                      ) : selectedConversation.caseNumber ? (
                        `Case #${selectedConversation.caseNumber} • ${selectedConversation.otherUserEmail}`
                      ) : (
                        selectedConversation.otherUserEmail
                      )}
                    </p>
                  </div>
                </div>
                {selectedConversation.caseId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/cases/${selectedConversation.caseId}`)}
                    className="hidden sm:flex"
                  >
                    View Case
                  </Button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {/* Client Type Indicator at top of message thread - for client and prospect conversations */}
                {!selectedConversation.isStaffConversation && (selectedConversation.otherUserRole === 'CLIENT' || selectedConversation.otherUserRole === 'PROSPECT') && (
                  <div className="flex justify-center mb-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-gray-200">
                      <span className="text-xs text-gray-500">{selectedConversation.otherUserRole === 'PROSPECT' ? 'Prospect' : 'Client'} Type:</span>
                      <ClientTypeBadge
                        caseStatus={selectedConversation.caseStatus}
                        isEnabled={selectedConversation.otherUserIsEnabled ?? true}
                        userRole={selectedConversation.otherUserRole}
                        size="small"
                      />
                    </div>
                  </div>
                )}
                {selectedConversation.messages.map((message) => {
                  const isFromMe = message.senderId === userData?.getCurrentUser?.id;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} group`}
                    >
                      <div className={`max-w-[85%] sm:max-w-[70%] ${isFromMe ? 'flex flex-col items-end' : ''}`}>
                        <div className="flex items-center gap-1">
                          {/* Edit and Delete buttons - shows on hover, before message if sent by me */}
                          {isFromMe && (
                            <>
                              <button
                                onClick={() => handleDeleteMessage(message)}
                                disabled={deleting}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-red-500"
                                title="Delete message"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleStartEdit(message)}
                                disabled={editing}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-blue-500"
                                title="Edit message"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </>
                          )}
                          <div
                            className={`rounded-lg px-4 py-2 shadow-sm ${
                              isFromMe
                                ? 'bg-blue-500'
                                : 'bg-white border border-gray-200'
                            }`}
                          >
                            <p className={`text-sm whitespace-pre-wrap break-words ${
                              isFromMe ? 'text-white' : 'text-gray-900'
                            }`}>
                              {message.content}
                            </p>
                            {message.isEdited && (
                              <p className={`text-xs mt-1 ${isFromMe ? 'text-blue-200' : 'text-gray-400'}`}>
                                (edited)
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 px-1">
                          <p className="text-xs text-gray-500">
                            {formatMessageTime(message.createdAt)}
                          </p>
                          {isFromMe && (
                            <span className="text-xs text-gray-500">
                              {message.isRead ? 'Read' : 'Unread'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex gap-2">
                  <Input
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="flex-1 bg-white text-base"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || sending}
                    style={{
                      backgroundColor: '#DC2626',
                      color: 'white',
                      opacity: (!messageText.trim() || sending) ? 0.5 : 1
                    }}
                    className="px-4 sm:px-6 hover:bg-red-700 whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">{sending ? '⏳ Sending...' : '📤 Send'}</span>
                    <span className="sm:hidden">{sending ? '⏳' : '📤'}</span>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2 hidden sm:block">Press Enter to send, Shift+Enter for new line</p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-4">
              <div>
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <p className="text-gray-500 font-medium">Select a conversation to start messaging</p>
                <Button
                  onClick={() => setShowMobileSidebar(true)}
                  className="md:hidden mt-4"
                >
                  View Conversations
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">New Message</h3>
                <button
                  onClick={() => {
                    setShowNewMessageModal(false);
                    setSelectedUserId('');
                    setMessageText('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* User Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Recipient
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choose a user...</option>
                    
                    {/* Staff Members */}
                    {staffData?.listStaff && staffData.listStaff.length > 0 && (
                      <optgroup label="Staff Members">
                        {staffData.listStaff
                          .filter((staff: any) => staff.id !== userData?.getCurrentUser?.id)
                          .map((staff: any) => (
                            <option key={staff.id} value={staff.id}>
                              {staff.firstName} {staff.lastName} ({staff.role})
                            </option>
                          ))}
                      </optgroup>
                    )}
                    
                    {/* Clients */}
                    {clientsData?.listClients && clientsData.listClients.length > 0 && (
                      <optgroup label="Clients">
                        {clientsData.listClients.map((client: any) => (
                          <option key={client.id} value={client.id}>
                            {client.firstName} {client.lastName}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* Message Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowNewMessageModal(false);
                      setSelectedUserId('');
                      setMessageText('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleStartNewConversation}
                    disabled={!selectedUserId || !messageText.trim() || sending}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      opacity: (!selectedUserId || !messageText.trim() || sending) ? 0.5 : 1
                    }}
                    className="px-4 py-2 rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      {editingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Edit Message</h3>
                <button
                  onClick={handleCancelEdit}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  placeholder="Edit your message..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  autoFocus
                />

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={!editText.trim() || editing}
                    style={{
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      opacity: (!editText.trim() || editing) ? 0.5 : 1
                    }}
                    className="px-4 py-2 rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed"
                  >
                    {editing ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
